// The 2x2 art grid that fills the right half of a navy page hero.
//
// Lifted verbatim out of the /learnings hero, which is where the pattern
// started, so every header that uses it lines up pixel for pixel: four equal
// 4:3 tiles, the same ring and shadow against the navy.
//
// Desktop only, by design. On a phone the hero is already a tall wall of text
// and four decorative tiles would push the CTA below the fold, so the grid
// drops out rather than stacking.
//
// The images are decorative: the heading beside them carries the meaning, and a
// screen reader reading out four scene descriptions would be noise. Hence
// alt="" and aria-hidden. Give it real alt text only if a tile ever becomes the
// content rather than the wallpaper.
interface HeroArtGridProps {
  /** Exactly four looks right; fewer renders a short grid, more is ignored. */
  images: string[]
  /**
   * Tile shape. 4:3 is the house default and what the generated art is drawn
   * to. Pass "16/9" for sources that are natively widescreen — YouTube
   * thumbnails cropped to 4:3 lose a third of their width, which slices the
   * episode title straight through the middle.
   */
  aspect?: '4/3' | '16/9'
}

export default function HeroArtGrid({ images, aspect = '4/3' }: HeroArtGridProps) {
  const tiles = images.filter(Boolean).slice(0, 4)
  if (!tiles.length) return null

  // Literal classes, not interpolated: Tailwind's JIT scans source text and
  // never sees a class built at runtime.
  const ratio = aspect === '16/9' ? 'aspect-[16/9]' : 'aspect-[4/3]'

  return (
    <div className="hidden lg:grid grid-cols-2 gap-4" aria-hidden="true">
      {tiles.map((src, i) => (
        <img
          key={src || i}
          src={src}
          alt=""
          className={`w-full ${ratio} object-cover rounded-xl ring-1 ring-white/15 shadow-2xl`}
          // Eager: it's above the fold, and lazy-loading it just guarantees a
          // visible pop-in on the first paint of every page that uses this.
          loading="eager"
        />
      ))}
    </div>
  )
}
